import { type RefObject, useEffect, useRef } from "react";
import { type DashMotion, dashSpeed, stepDashMotion } from "./flowMotion";

/** Moves a dashed SVG line at the pace `periodS` gives, one dash cycle
 *  per period, and eases between paces so the dashes slow down or speed
 *  up instead of snapping when a poll re-derives the pace. Returns the
 *  ref to put on the line.
 *
 *  A CSS animation would not do: changing its duration keeps the start
 *  time and re-maps the elapsed time onto the new period, which throws
 *  the phase anywhere in the cycle, and taking it off snaps the offset
 *  to zero. Driving the offset a frame at a time keeps the phase
 *  continuous by construction. The frames stop while the line is at
 *  rest, so a still connector costs nothing. */
export function useDashMotion(
  periodS: number | null,
  cyclePx: number,
): RefObject<SVGLineElement> {
  const line = useRef<SVGLineElement>(null);
  const motion = useRef<DashMotion>({ phase: 0, speed: 0 });
  const frame = useRef<number | null>(null);
  const lastT = useRef<number | null>(null);

  // One effect owns the frame loop: a new pace restarts it (the motion
  // lives in refs, so the phase carries over and the first frame's dt is
  // zero), and its cleanup cancels it. StrictMode's double run in dev,
  // unmounting, and a loop that stopped at rest all go through the same
  // path, so no stale frame handle can keep the loop from starting.
  useEffect(() => {
    const targetSpeed = dashSpeed(periodS, cyclePx);
    const tick = (t: number) => {
      const dtS = lastT.current === null ? 0 : (t - lastT.current) / 1000;
      lastT.current = t;
      const next = stepDashMotion(motion.current, targetSpeed, dtS, cyclePx);
      motion.current = next;
      line.current?.style.setProperty("stroke-dashoffset", `${-next.phase}px`);
      if (next.speed === 0 && targetSpeed === 0) {
        frame.current = null;
        lastT.current = null;
        return;
      }
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
      lastT.current = null;
    };
  }, [periodS, cyclePx]);

  return line;
}
